"use client";

import { useEffect } from "react";

/**
 * Registers the service worker that keeps build assets on the phone (see public/sw.js).
 *
 * Renders nothing. Registration is deliberately deferred until after load so it never competes
 * with the first paint for bandwidth on a cold launch, which would defeat the point.
 *
 * ESCAPE HATCH: loading any page with ?nosw=1 unregisters the worker and deletes its caches.
 * That is the recovery path if a bad asset ever gets pinned, and it works from inside the app's
 * WebView where there is no browser UI to clear storage with.
 */
export default function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    const disable = new URLSearchParams(window.location.search).get("nosw") === "1";

    if (disable) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => registration.unregister());
      });
      if ("caches" in window) {
        caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
      }
      return;
    }

    // The dev server must never sit behind the worker: dev chunk names are stable, so a
    // cache-first worker pins week-old JavaScript and the site silently stops updating.
    // That is exactly what happened when the site was reviewed over the LAN address,
    // which is not "localhost" and so sailed past the old hostname check. In development
    // the worker is actively torn down, not just skipped, to heal any device that
    // already has one.
    if (process.env.NODE_ENV !== "production") {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => registration.unregister());
      });
      if ("caches" in window) {
        caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
      }
      return;
    }

    const register = () => {
      navigator.serviceWorker.register("/sw.js").catch(() => {
        // A failed registration must never break the page: the app works exactly as it does
        // today without a worker, just without the local asset cache.
      });
    };

    if (document.readyState === "complete") {
      register();
    } else {
      window.addEventListener("load", register, { once: true });
      return () => window.removeEventListener("load", register);
    }
  }, []);

  return null;
}
